import S from '@/settings'
const ERRORS = {
  UNKNOWN_ERROR: 'Unknown error',
  SESSION_NOT_FOUND: 'Session not found',
  SIGNUP: {
    INVALID_EMAIL: 'Invalid email format',
    INVALID_FULL_NAME: 'Invalid Full Name',
    INVALID_PASSWORD: 'Invalid password: Password must be between 6 ~ 255 characters',
    INVALID_USERNAME:
      'Invalid username: Username must be between 4 ~ 31 characters, ' +
      'and only consists of lowercase letters, 0-9, -, and _',
  },
  DOCUMENT: {
    UPLOAD_FILE: 'Error uploading document content',
    UPDATE: 'Error updating document',
    PREVIEW_DELETE: 'Error previewing document delete',
    DELETE: 'Error deleting document',
    REAL_WORK_REQUIRED: 'Confirmation required: this document has user-modified data',
  },
  LOGIN: {
    INVALID_CREDENTIALS: 'Invalid credentials',
    TOKEN_EXPIRED: 'This login link has expired. Please request a new one.',
    TOKEN_ALREADY_USED: 'This login link has already been used. Please request a new one.',
    ACCESS_REVOKED: 'This account no longer has access. Please contact your administrator.',
  },
  FILE_SIZE_LIMIT_EXCEEDED:
    `File size limit exceeded. Please Upload a file with size less than` + ` ${S.FILE_SIZE_LIMIT_IN_MB} MB`,
  FILE_UPLOAD: {
    NO_EXTENSION: 'File must have an extension',
    TYPE_NOT_ALLOWED: 'File type not allowed',
    CONTENT_NOT_VERIFIED: 'File content could not be verified',
    CONTENT_MISMATCH: 'File content does not match extension',
    ONLY_ALLOWED_QUESTIONNAIRE_TYPES: 'Only Excel, Word, PDF, or OpenDocument files are allowed',
    MUST_BE_VALID_QUESTIONNAIRE_TYPE: 'File content does not match an allowed questionnaire format',
    ONLY_CSV: 'Only .csv files are allowed',
    ONLY_PDF_OR_MD: 'Only PDF or Markdown (.md) files are allowed',
    FILE_TOO_LARGE: 'File is too large',
  },
  FETCH_CONTROL: 'Cannot load control',
  FETCH_DOCUMENT: 'Cannot load document',
  FETCH_EVIDENCE: 'Cannot load evidence',
  FETCH_TASK: 'Cannot load task',
  FETCH_EMPLOYEE: 'Cannot load employee',
  AUTOMATIC_PENTESTING: 'Error getting automated pentesting data',
  MONITORING: 'Error getting monitoring data',
  PASSWORD_MANAGER: 'Error getting password manager data',
  MDM: 'Error getting MDM enrollment data',
  TRAINING: 'Error getting training data',
  CODE_SCANNER: 'Error getting code scanner data',
  PHISHING_EXERCISES: 'Error getting phishing exercises data',
  INFRASTRUCTURE: 'Error getting infrastructure data',
  IDENTITIES: 'Error getting identities data',
  CONTROLS: {
    LIST: 'Error getting controls',
    UPDATE: 'Error updating control',
    GET_DOC_EVIDENCE: 'Error getting documents and evidences by control id',
    GET: 'Error retrieving control control',
    PREVIEW_DELETE: 'Error previewing control delete',
  },
  EVIDENCES: {
    FETCH: 'Error getting evidence',
    CREATE: 'Error creating evidence',
    UPDATE: 'Error updating evidence',
    LIST: 'Error getting evidences',
    SIZE_LIMIT: `File size limit exceeded. Please Upload a file with size less than` + ` ${S.FILE_SIZE_LIMIT_IN_MB} MB`,
    BULK_EDIT_OWNER: 'Error updating evidences owner',
    UPLOAD_FILE: 'Error uploading evidence content',
    PREVIEW_DELETE: 'Error previewing evidence delete',
    DELETE: 'Error deleting evidence',
    REAL_WORK_REQUIRED: 'Confirmation required: this evidence has user-modified data',
  },
  TASKS: {
    LIST: 'Error getting tasks',
    CREATE: 'Error creating task',
    GET: 'Error getting task',
    UPDATE: 'Error updating task',
    CREATE_BULK: 'Error creating tasks',
    DELETE: 'Error deleting task',
  },
  BULK_EDIT: {
    OWNER: 'Error updating owner',
    STATUS: 'Error updating status',
  },
  NON_CONFORMITIES: {
    LIST: 'Error getting non-conformities',
    GET: 'Error getting non-conformity',
    CREATE: 'Error creating non-conformity',
    UPDATE: 'Error updating non-conformity',
    DELETE: 'Error deleting non-conformity',
    NOT_FOUND: 'Non-conformity not found',
  },
  OPEN_SEARCH_AUTH: {
    FRONT: 'Dashboard Authentication error',
    SECRET_NOT_DEFINED: 'OPENSEARCH_SECRET is not defined',
    INVALID_CAPABILITY: (capability: string) => `Invalid capability: ${capability}`,
  },
  RISK: {
    FETCH: 'Error fetching risk',
    UPDATE: 'Error updating risk',
    UNKNOWN_CATALOGUE_SCENARIO: 'Unknown catalogue scenario',
  },
  USER_EMAIL_ALREADY_EXISTS: 'User email already exists',
  USER_EMAIL_INVALID: 'User email is invalid',
  USER_ROLE_REQUIRED: 'You must select a role',
  USERS: {
    LIST: 'Error getting users',
    CREATE: 'Error creating user',
    DELETE: 'Error deleting user',
    UPDATE_ROLE: 'Error updating user role',
    INVALID_ROLE: 'This role cannot be assigned',
    ROLE_LOCKED: "Contact support to change this user's role",
    SERVICE_ACCOUNT_EMAIL: 'This email belongs to a service account',
  },
  SERVICE_ACCOUNTS: {
    MARK: 'Error marking the employee as a service account',
    IN_USE: 'This employee is still referenced elsewhere and cannot be marked as a service account',
    CANNOT_MARK_SELF: 'You cannot mark yourself as a service account',
    LIST: 'Error getting service accounts',
    RESTORE: 'Error moving the service account back to the employees',
    EMAIL_TAKEN: 'An employee with this email already exists',
  },
  API_KEYS: {
    NO_ACCESS: 'Your organization does not have access to the public API',
    INVALID_NAME: 'The API key name must have between 1 and 100 characters',
    NOT_FOUND: 'API key not found',
    CREATE: 'Error creating API key',
    LIMIT_REACHED: 'You already have 10 active API keys. Revoke one to create another',
    LIST: 'Error getting API keys',
    REVOKE: 'Error revoking API key',
  },
  PUBLIC_API_ENDPOINTS: {
    LIST: 'Error getting public API endpoint settings',
    UPDATE: 'Error updating the public API endpoint setting',
    NOT_FOUND: 'Public API endpoint not found',
  },
  EMPLOYEES: {
    UPDATE: 'Error updating employee',
    CREATE_ONBOARDING_TASKS: 'Error generating onboarding tasks',
    CREATE_OFFBOARDING_TASKS: 'Error generating offboarding tasks',
    ADD_CHECKLIST_ITEM: 'Error adding checklist item',
    UPDATE_CHECKLIST_ITEM: 'Error updating checklist item',
    REMOVE_CHECKLIST_ITEM: 'Error removing checklist item',
    REORDER_CHECKLIST: 'Error reordering checklist',
    RESET_CHECKLIST: 'Error resetting checklist',
    FETCH_TASK_EVIDENCE: 'Could not load task evidence',
    UPDATE_TASK_EVIDENCE: 'Could not update task evidence',
    UPLOAD_TASK_EVIDENCE_FILE: 'Could not upload task evidence file',
    REMOVE_TASK_EVIDENCE_FILE: 'Could not remove task evidence file',
  },
  INVALID_FILE_TYPE: 'File type is not allowed',
  FILE_TOO_LARGE: 'File is too large',
  HARDENING: {
    LIST: 'Error getting hardening devices',
    GET: 'Error getting hardening device',
    NOT_LOADED: 'Error loading asset',
  },
  APPSEC: {
    GET: 'Error getting dashboard',
    MUTE: 'Error muting finding',
    UNMUTE: 'Error unmuting finding',
  },
  CSPM: {
    GET: 'Error getting dashboard data',
    GET_FINDINGS: 'Error getting findings',
    GET_FINDING_DETAIL: 'Error getting finding details',
    EMPTY_DASHBOARD: 'No dashboard data found',
    MUTE: 'Error muting finding',
    UNMUTE: 'Error unmuting finding',
  },
  EASM: {
    MUTE: 'Error muting finding',
    UNMUTE: 'Error unmuting finding',
    REVIEW_BREACH: 'Error marking breach as reviewed',
  },
  INFRASTRUCTURE_VULNERABILITIES: {
    MUTE: 'Error muting vulnerability',
    UNMUTE: 'Error unmuting vulnerability',
  },
  HOME: 'Error getting home dashboard data',
  EXECUTIVE_REPORT: 'Error requesting the executive report',
}
export default ERRORS
